import React from 'react'
import classnames from 'classnames'
import Panel from '../../../components/Panel'
import QuickSearch from '../../../components/QuickSearch/QuickSearch'
import { ComboboxAutocomplete } from '../../../components/Form/ComboboxAutocomplete'
import type { TariffStatus, Utility } from '../../../apis/types'
import { FieldTooltip } from './TariffFieldKey'
import { tariffStatusLabel } from './TariffChips'

const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300'

/** API / DB utility type values used in list filters. DA maps to ESP_DA. */
export type UtilityTypeFilter = 'ALL' | 'IOU' | 'CCA' | 'ESP_DA'

export const UTILITY_TYPE_HINTS: Record<'IOU' | 'CCA' | 'ESP_DA', string> = {
    IOU: 'Investor-Owned Utility',
    CCA: 'Community Choice Aggregator',
    ESP_DA: 'Direct Access',
}

const TYPE_OPTIONS: Array<{ value: UtilityTypeFilter; label: string; tooltip: string }> = [
    { value: 'ALL', label: 'All', tooltip: 'All utility types' },
    { value: 'IOU', label: 'IOU', tooltip: UTILITY_TYPE_HINTS.IOU },
    { value: 'CCA', label: 'CCA', tooltip: UTILITY_TYPE_HINTS.CCA },
    { value: 'ESP_DA', label: 'DA', tooltip: UTILITY_TYPE_HINTS.ESP_DA },
]

const STATUS_OPTIONS: Array<{ value: TariffStatus | ''; label: string; tooltip: string }> = [
    { value: '', label: 'All', tooltip: 'All tariff schedules regardless of status.' },
    {
        value: 'PENDING',
        label: tariffStatusLabel('PENDING'),
        tooltip: 'Drafted or ingested ratesets pending ops review.',
    },
    {
        value: 'ACTIVE',
        label: tariffStatusLabel('ACTIVE'),
        tooltip: 'Approved for billing for the rateset date window.',
    },
    {
        value: 'INACTIVE',
        label: tariffStatusLabel('INACTIVE'),
        tooltip: 'Withdrawn or retired schedules.',
    },
]

const segmentButtonClass = (
    selected: boolean,
    index: number,
    total: number
): string =>
    classnames(
        'border px-2.5 py-1.5 text-sm font-medium whitespace-nowrap focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-500',
        index === 0 && 'rounded-l-md',
        index === total - 1 && 'rounded-r-md',
        index > 0 && '-ml-px',
        selected
            ? 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-500'
            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800'
    )

export const TariffFilterBar: React.FC<{
    search: string | null
    utilityType: UtilityTypeFilter
    utilityCuid: string | null
    status: TariffStatus | ''
    utilities: Utility[]
    hasActiveFilters: boolean
    onSearchChange: (value: string) => void
    onUtilityTypeChange: (value: UtilityTypeFilter) => void
    onUtilityCuidChange: (value: string | undefined) => void
    onStatusChange: (value: TariffStatus | '') => void
    onClearFilters: () => void
}> = ({
    search,
    utilityType,
    utilityCuid,
    status,
    utilities,
    hasActiveFilters,
    onSearchChange,
    onUtilityTypeChange,
    onUtilityCuidChange,
    onStatusChange,
    onClearFilters,
}) => (
    <Panel
        className="mb-5"
        header={
            <div className="flex w-full items-center justify-between gap-3">
                <span>Filters</span>
                {hasActiveFilters ? (
                    <button
                        type="button"
                        onClick={onClearFilters}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    >
                        Clear filters
                    </button>
                ) : null}
            </div>
        }
    >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
                <div className={labelClass}>Utility type</div>
                <div className="inline-flex rounded-md shadow-sm" role="group" aria-label="Utility type">
                    {TYPE_OPTIONS.map((opt, index) => (
                        <FieldTooltip key={opt.value} content={opt.tooltip}>
                            <button
                                type="button"
                                onClick={() => onUtilityTypeChange(opt.value)}
                                className={segmentButtonClass(
                                    utilityType === opt.value,
                                    index,
                                    TYPE_OPTIONS.length
                                )}
                            >
                                {opt.label}
                            </button>
                        </FieldTooltip>
                    ))}
                </div>
            </div>
            <div className="min-w-0">
                <div className={labelClass}>Status</div>
                <div className="inline-flex max-w-full flex-wrap rounded-md shadow-sm" role="group" aria-label="Status">
                    {STATUS_OPTIONS.map((opt, index) => (
                        <FieldTooltip key={opt.value === '' ? 'all' : opt.value} content={opt.tooltip}>
                            <button
                                type="button"
                                onClick={() => onStatusChange(opt.value)}
                                className={segmentButtonClass(
                                    status === opt.value,
                                    index,
                                    STATUS_OPTIONS.length
                                )}
                            >
                                {opt.label}
                            </button>
                        </FieldTooltip>
                    ))}
                </div>
            </div>
            <div>
                <label className={labelClass}>Utility</label>
                <ComboboxAutocomplete
                    labelPlural="Utilities"
                    value={utilityCuid ?? ''}
                    handleChange={(id) =>
                        onUtilityCuidChange(id != null && id !== '' ? String(id) : undefined)
                    }
                    data={utilities.map((u) => ({
                        id: u.id,
                        name:
                            u.shortName != null && u.shortName !== ''
                                ? `${u.shortName} — ${u.name}`
                                : u.name,
                    }))}
                />
            </div>
            <QuickSearch
                key={search ?? ''}
                title="Schedule, name, cuid, rate, season, TOU, or group"
                placeholder="e.g. B19, cuid, SUMMER, PEAK, 0.12"
                initialValue={search ?? undefined}
                handleChange={onSearchChange}
            />
        </div>
    </Panel>
)
